import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';
import { Product } from '../../models/product.model';

export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'DD/MM/YYYY') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return super.format(date, displayFormat);
  }

  override parse(value: any): Date | null {
    if (typeof value === 'string' && value.length > 0) {
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          const date = new Date(year, month, day);
          if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
            return date;
          }
        }
      }
    }
    return super.parse(value);
  }
}

@Component({
  selector: 'app-add-poliza-modal',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule, 
    MatButtonModule, 
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE, Platform]
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY',
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'DD/MM/YYYY',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
  templateUrl: './add-poliza-modal.component.html',
  styleUrl: './add-poliza-modal.component.scss'
})
export class AddPolizaModalComponent {
  selectedProduct: Product | null = null;
  availableProducts: Product[] = [];
  isNewPoliza: boolean = false;
  minDate: Date;
  
  // Campos para nueva póliza
  polizaNumber: string = '';
  startDate: Date | null = null;
  endDate: Date | null = null;
  
  constructor(
    public dialogRef: MatDialogRef<AddPolizaModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { products: Product[], isNewPoliza?: boolean }
  ) {
    this.availableProducts = data.products || [];
    this.isNewPoliza = data.isNewPoliza || false;
    
    // Configurar fecha mínima como hoy
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.minDate = today;
  }

  onStartDateChange(date: Date | null): void {
    if (date) {
      const endDate = new Date(date);
      endDate.setFullYear(endDate.getFullYear() + 1);
      endDate.setDate(endDate.getDate() - 1);
      this.endDate = endDate;
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(): void {
    if (this.isNewPoliza) {
      if (this.isValidNewPoliza()) {
        this.dialogRef.close({
          polizaNumber: this.polizaNumber,
          product: this.selectedProduct,
          startDate: this.formatDate(this.startDate!),
          endDate: this.formatDate(this.endDate!),
          reloadList: true
        });
      }
    } else {
      if (this.selectedProduct) {
        this.dialogRef.close({
          product: this.selectedProduct.product,
          name: this.selectedProduct.name,
          active: true,
          reloadList: true
        });
      }
    }
  }

  private isValidNewPoliza(): boolean {
    return !!(
      this.polizaNumber && 
      this.selectedProduct && 
      this.startDate && 
      this.endDate && 
      this.endDate > this.startDate
    );
  }

  private formatDate(date: Date): string {
    return date.toISOString().slice(0, 19).replace('T', ' ');
  }

  get isFormValid(): boolean {
    if (this.isNewPoliza) {
      return this.isValidNewPoliza();
    }
    return !!this.selectedProduct;
  }
}